import { keyBy, get, mergeWith, difference, map, compact, defaultsDeep, upperFirst, snakeCase, isPlainObject, isNil } from 'lodash';
import { isBlank } from '@/lib/utils';
import { plural, singular } from 'pluralize';
import I18n from '@/i18n';

import RestModule from '@/store/rest.module';
import RestIdModule from '@/store/rest.id.module';

import { booleanFormatter, dateFormatter, datetimeFormatter, resourceFormatter } from '@/utils/formatters';
// import { resourceDeserializer } from '@/utils/json-api';

const columnWidhs = {
  small: 90,
  medium: 140,
  big: 230,
  date: 190,
};

const formatters = {
  date: dateFormatter,
  datetime: datetimeFormatter,
  boolean: booleanFormatter,
};

const attributesDefaults = {
  code: {
    table: { size: 'small' },
  },
  description: {
    type: 'text',
    form: { typeOptions: { autosize: { minRows: 4 } } },
  },
  notes: {
    type: 'text',
    form: { typeOptions: { autosize: { minRows: 2 } } },
  },
  createdAt: {
    type: 'date',
    form: false,
  },
  updatedAt: {
    type: 'date',
    form: false,
  },
  deletedAt: {
    type: 'date',
    form: false,
  },
};

/*
const attrDef = {
  key,
  prop,
  type,
  formatter,
  label,
  table: {
    label,
    size,
    filter,
    sort,
  },
  form: {
    label
    type,
    typeOptions,
    validate,
  },
  server: {
    serialize,
    deserialize,
    filterSerialize,
    filterKey,
    sortKey,
  }
  routeFilter: {
    serialize,
    deserialize,
  }
}
*/

function computeEnumI18n({
  value, name, namespace = [],
}) {
  const base = map(['models', 'enums', ...namespace, name, value], snakeCase).join('.');
  if (I18n.te(base)) return I18n.t(base);
  const i18n = map(['models', 'enums', name, value], snakeCase).join('.');
  if (I18n.te(i18n)) return I18n.t(i18n);
  return I18n.t(base);
}

function computeAttributeI18n({
  key, name, namespace, default: dflt,
}) {
  const lastKey = key.split('.').pop();
  const base = map(['models', 'attributes', ...namespace, name, key], snakeCase).join('.');
  if (I18n.te(base)) return base;
  let i18n = map(['models', 'attributes', ...namespace, name, lastKey], snakeCase).join('.');
  if (I18n.te(i18n)) return i18n;
  i18n = map(['models', 'attributes', key], snakeCase).join('.');
  if (I18n.te(i18n)) return i18n;
  i18n = map(['models', 'attributes', lastKey], snakeCase).join('.');
  if (I18n.te(i18n)) return i18n;
  if (I18n.te(dflt)) return dflt;
  return base;
}

const typeDefaults = {
  string: {
    form: { type: 'input' },
  },
  password: {
    form: { type: 'input', typeOptions: { type: 'password' } },
  },
  email: {
    form: { type: 'input', typeOptions: { type: 'email' } },
  },
  text: {
    form: { type: 'textarea', typeOptions: { autosize: { minRows: 2 } } },
    table: { size: 'big' },
  },
  date: {
    formatter: 'date',
    table: { size: 'date' },
  },
  icon: (opts = {}) => ({
    table: {
      type: 'icon',
      label: false,
      minWidth: 60,
      sort: false,
      filter: false,
      opts: {
        tooltip: true,
        size: 14,
        ...opts,
      },
    },
  }),
  select: ({ options } = {}) => ({
    type: {
      name: 'select',
      options,
    },
  }),
  enum: ({ options, namespace } = {}, def) => {
    const { key: name } = def;
    return ({
      formatterFn(value) {
        if (!value) return null;
        return computeEnumI18n({ value, name, namespace });
      },
      type: {
        name: 'select',
        options: options.map(value =>
          ({ value, label: computeEnumI18n({ value, name, namespace }) })),
      },
    });
  },
  boolean: {
    formatter: 'boolean',
    form: { type: 'switch', typeOptions: { trueLabel: 'Oui', falseLabel: 'Non' } },
    table: {
      size: 'small',
      filter: {
        type: 'select',
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        multiple: false,
      },
    },
  },
  units: {
    formatter: 'units',
    table: { size: 'big', align: 'right' },
    server: {
      serialize(data, { config: { key, prop } }) {
        const attr = prop || key;
        const { value, unit } = data || {};
        return {
          [`${attr}`]: value,
          [`${attr}Unit`]: unit,
        };
      },
      deserialize(value, { config: { key, prop }, data }) {
        if (isNil(value)) return value;
        const attr = prop || key;
        const unit = get(data, `${attr}Unit`);
        return { value, unit };
      },
      filterSerialize(key, data) {
        const { op, query } = data;
        const { value, unit } = query;
        return op ?
          { [`${key}_${op}`]: `${value}_${unit}` }
          : { [key]: `${value}_${unit}` };
      },
    },
    routeFilter: {
      serialize(data) {
        const { value, unit } = data;
        return `${value}_${unit}`;
      },
      deserialize(data) {
        const [value, unit] = data.split('_');
        if (isBlank(value) || isBlank(unit)) return null;
        return { value, unit };
      },
    },
  },
  resource: (opts, def) => {
    const { key } = def;
    const {
      namespace,
      config,
      multiple = false,
      formatFn = ({ code, name }) => `(${code}) ${name}`,
    } = opts;

    const singularKey = multiple ? singular(key) : key;
    const { prop = (multiple ? `${singularKey}Ids` : `${singularKey}Id`) } = def;

    let resourceOptions = {};

    let { store = false, remote = true } = opts;
    if (store) {
      const { name = singularKey.split('.').pop() } = opts;
      if (store === true) store = [...namespace, plural(name)].join('/');
      resourceOptions = {
        prop,
        type: {
          options() {
            const format = data => ({ value: data.id, label: formatFn(data) });
            const options = this.$store.getters[store] || [];
            return options.map(format);
          },
        },
        formatter: multiple ?
          (...args) => ((resourceFormatter(store, formatFn))(...args) || []).join(', ') :
          resourceFormatter(store, formatFn),
        i18n: map(['models', ...namespace, name], snakeCase).join('.'),
      };
    } else if (remote) {
      const { prop: localProp = def.key } = def;
      const { url } = config.store;

      if (remote === true) remote = {};
      const {
        deserialize = data => ({ value: data.id, label: formatFn(data) }),
        params = ({ query, values }) => ({ q: query, ids: values }),
      } = remote;

      resourceOptions = {
        prop,
        type: {
          filterable: true,
          remote: {
            url,
            deserialize: ({ attributes: attrs }) => deserialize(attrs),
            params,
          },
        },
        table: { prop: localProp },
        formatter: multiple ? (value = []) => value.map(formatFn).join(', ') : formatFn,
        ...(multiple ? {} : {
          server: {
            // deserialize: data => resourceDeserializer(data, attributes),
            filterKey: `${singularKey}Id`,
          },
        }),
      };
    }

    return defaultsDeep(resourceOptions, {
      type: {
        name: 'select',
      },
      form: { typeOptions: { filterable: true, multiple } },
      table: { size: 'big', sort: !multiple },
      ...(multiple ? {} : {
        server: {
          sortKey: `${singularKey}.code`,
        },
      }),
    });
  },
};

function mergeConfig(...args) {
  function customizer(objValue, srcValue) {
    if (Array.isArray(objValue) && srcValue) {
      return srcValue;
    }
    return undefined;
  }
  return mergeWith(...args, customizer);
}

function computeTypeOptions(config) {
  if (typeof (config) === 'string') {
    return { key: config, type: config };
  } else if (Array.isArray(config)) {
    const [key, ...tmp] = config;
    let [type = key, options = {}] = tmp;
    if (isPlainObject(type)) { options = type; type = key; }
    return { key, type, ...options };
  }
  return config;
}

function computeTypeDefaults(type = 'string', def = {}) {
  if (isPlainObject(type)) return { type };
  const [name, opts = {}] = Array.isArray(type) ? type : [type];

  let defaults = typeDefaults[name];
  if (!defaults) return { type: { name: 'string', ...opts }, ...typeDefaults.string };

  if (typeof (defaults) === 'function') defaults = defaults(opts, def);

  return { type: { name, ...opts }, ...defaults };
}

function computeDefaults(config) {
  const { type: attr, ...options } = computeTypeOptions(config);
  const { type, ...attrDefaults } = attributesDefaults[attr] || { type: attr };
  const def = defaultsDeep(options, attrDefaults);
  const defaults = computeTypeDefaults(type, def);
  return defaultsDeep(def, defaults);
}

function computeColumnDef({ size = 'medium', ...config }) {
  return {
    size,
    minWidth: columnWidhs[size] || size,
    ...config,
  };
}

function computeFieldDef({ ...config }) {
  return {
    ...config,
  };
}

function computeAttributeDefs({ name, namespace, attributes }) {
  return attributes.map((attr) => {
    const defaults = computeDefaults(attr);

    const {
      key,
      type,
      formatter,
      required,
      i18n,
    } = defaults;

    const {
      prop = key,
      label = computeAttributeI18n({
        key, name, namespace, default: i18n,
      }),
      formatterFn = (typeof (formatter) === 'string') ? formatters[formatter] : formatter,
      table = {},
      form = {},
    } = defaults;

    const { name: typeName, ...typeOptions } = type;
    const defaultType = { type: typeName, ...typeOptions };

    const attrDef = {
      prop,
      label,
      formatterFn,
      table: (({ filter = {}, ...rest }) => computeColumnDef({
        key,
        prop,
        label,
        formatterFn,
        filter: filter.type ? filter : defaultsDeep({}, filter, defaultType),
        ...rest,
      }))(table),
      form: (formOptions => computeFieldDef(defaultsDeep(formOptions, {
        name: key,
        prop,
        label,
        type: typeName,
        typeOptions,
        validate: required ? { required } : {},
      })))(form),
    };

    return defaultsDeep(defaults, attrDef);
  });
}

function orderFields(fields, order) {
  const fieldsByName = keyBy(fields, 'name');
  const names = Object.keys(fieldsByName);
  const diff = difference(names, order);
  const fieldsOrder = [...order, ...diff];
  return fieldsOrder
    .map(name => fieldsByName[name])
    .filter(field => !!field);
}

export default ({
  attributes = [], table = {}, form = {}, ...config
}) => {
  const {
    name, namespace = [], key, defaultOrder, deletable = true,
  } = config;

  const names = plural(name);
  const namespaced = [...namespace, names];

  const timestampsAttributes = ['createdAt', 'updatedAt'];
  if (deletable) timestampsAttributes.push('deletedAt');

  const attributesDefs = computeAttributeDefs({
    name,
    namespace,
    attributes: [...attributes, ...timestampsAttributes],
  });

  const { columns: tableAttributes = [], ...tableConfig } = table;
  const columnsDefs = computeAttributeDefs({ attributes: tableAttributes, name, namespace });
  const columns = map([...attributesDefs, ...columnsDefs], 'table') |> compact;

  const { fields: formAttributes = [], ...formConfig } = form;
  const fieldsDefs = computeAttributeDefs({ attributes: formAttributes, name, namespace });
  const fields = map([...attributesDefs, ...fieldsDefs], 'form') |> compact;

  return mergeConfig({
    route: {
      name: namespaced.join('-'),
      path: names,
      i18n: map(['pages', ...namespaced], snakeCase).join('.'),
      key,
    },
    store: {
      namespace: map(['pages', ...namespaced], upperFirst).join('/'),
      url: `/${namespaced.join('/')}`,
      modules: {
        index: RestModule,
        id: RestIdModule,
      },
    },
    resource: {
      attributes: attributesDefs,
    },
    table: {
      columns,
      defaultOrder: defaultOrder || difference(map(columns, 'key'), timestampsAttributes),
      defaultSort: [[key, 'asc']],
    },
    form: {
      fields: orderFields(fields, formConfig.defaultOrder || defaultOrder || map(fields, 'name')),
    },
    server: {
      attributes: [...attributesDefs, ...columnsDefs, ...fieldsDefs]
        .map(({ key: k, prop, server = {} }) => ({ key: k, prop, ...server })),
    },
    attributesDefs: [...attributesDefs, ...columnsDefs, ...fieldsDefs],
  }, {
    ...config,
    table: tableConfig,
    form: formConfig,
  });
};
